import { useState, type ReactNode } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "../components/ui/card";
import { Label } from "../components/ui/label";
import { Skeleton } from "../components/ui/skeleton";
import { PageHeader } from "../components/ui/page-header";
import {
  ExpenseForm,
  type ExpenseFormInitialDraft,
} from "../components/ExpenseForm";
import { ReceiptScanBar } from "../components/ReceiptScanBar";
import { useCreateExpense, useGroup, useMe, useMyGroups } from "../api/hooks";
import { getMyParticipantId } from "../lib/groupRole";
import type { Group, GroupListItem } from "../api/types";

/**
 * "New expense" flow. Reachable two ways:
 *
 * - Top-level `/expenses/new` (from the FAB): the group is chosen here via a
 *   selector at the top, optionally preselected with `?groupId=…`.
 * - Nested `/groups/:groupId/expenses/new` (a child of the group): the group is
 *   fixed by the URL, so the selector is hidden and Back returns to the group.
 *
 * Receipt scanning is part of this screen: once a group is set, a "Fill from a
 * receipt" bar sits above the form with buttons to snap a photo or upload an
 * image / PDF. A successful scan prefills the form in place.
 */
export function CreateExpensePage(): ReactNode {
  const [searchParams] = useSearchParams();
  // Present only on the nested `/groups/:groupId/expenses/new` route. When set,
  // it pins the expense to that group (no selector).
  const { groupId: routeGroupId } = useParams<{ groupId?: string }>();
  const navigate = useNavigate();
  const groupsQ = useMyGroups();

  const groups = groupsQ.data?.items ?? [];
  const lockedToGroup = routeGroupId !== undefined;

  // `picked` is an explicit selection; until then follow `?groupId=` (from the
  // FAB) and finally fall back to the first group. On the nested route the
  // group is fixed by the path and the selector never shows.
  const [picked, setPicked] = useState<string | undefined>(undefined);
  const fromParam = routeGroupId ?? searchParams.get("groupId") ?? undefined;
  const paramGroupId =
    fromParam && groups.some((g) => g.id === fromParam) ? fromParam : undefined;
  const selectedGroupId = lockedToGroup
    ? (routeGroupId ?? "")
    : (picked ?? paramGroupId ?? groups[0]?.id ?? "");

  const groupQ = useGroup(selectedGroupId || undefined);
  // Where "Back" returns to: the group it's nested under, else the dashboard.
  const backTo = lockedToGroup ? `/groups/${routeGroupId}` : "/";

  if (groupsQ.isLoading) {
    return (
      <div className="mx-auto max-w-2xl space-y-stack-lg">
        <PageHeader title="New expense" />
        <Skeleton className="h-64" />
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <div className="mx-auto max-w-2xl space-y-stack-lg">
        <PageHeader
          back={{ label: "Back", onClick: () => navigate(backTo) }}
          title="New expense"
        />
        <Card>
          <CardContent className="p-6 text-body-md text-on-surface-variant">
            Create a group first, then add expenses straight into it.
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-2xl space-y-stack-lg">
      <PageHeader
        back={{ label: "Back", onClick: () => navigate(backTo) }}
        title="New expense"
        description="Snap or upload a receipt to fill it in automatically, or add items by hand. Cross-currency expenses convert to the group currency at the rate you supply."
      />

      {lockedToGroup ? null : (
        <GroupSelector
          groups={groups}
          value={selectedGroupId}
          onChange={setPicked}
        />
      )}

      {groupQ.isLoading || !groupQ.data ? (
        <Skeleton className="h-96" />
      ) : (
        <CreateExpense
          // Re-mount when the chosen group changes so payer/item state never
          // carries over between groups with different rosters.
          key={groupQ.data.id}
          group={groupQ.data}
        />
      )}
    </div>
  );
}

function GroupSelector({
  groups,
  value,
  onChange,
}: {
  groups: GroupListItem[];
  value: string;
  onChange: (groupId: string) => void;
}): ReactNode {
  return (
    <div className="space-y-2">
      <Label htmlFor="expense-group">Group</Label>
      <select
        id="expense-group"
        className="w-full rounded-md border border-outline bg-surface px-3 py-2 text-body-md"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      >
        {groups.map((g) => (
          <option key={g.id} value={g.id}>
            {g.label}
          </option>
        ))}
      </select>
    </div>
  );
}

function CreateExpense({ group }: { group: Group }): ReactNode {
  const navigate = useNavigate();
  const meQ = useMe();
  const createExpense = useCreateExpense(group.id);
  const groupPath = `/groups/${group.id}`;

  // A scan result prefills the form in place. Bumping `draftNonce` re-mounts the
  // form so its mount-time prefill picks up the new draft (the form reads
  // `initialDraft` only on mount, by design).
  const [draft, setDraft] = useState<ExpenseFormInitialDraft | undefined>(
    undefined,
  );
  const [draftNonce, setDraftNonce] = useState(0);

  function applyDraft(next: ExpenseFormInitialDraft): void {
    setDraft(next);
    setDraftNonce((n) => n + 1);
  }

  // Re-mount the form when the group's participant set changes so a refetch
  // that adds or removes a participant doesn't leave stale `payerId`s.
  const participantsKey = group.participants
    .map((p) => p.id)
    .sort()
    .join("|");

  return (
    <div className="space-y-stack-lg">
      <ReceiptScanBar group={group} onScanned={applyDraft} />
      <ExpenseForm
        key={`${participantsKey}:${draftNonce}`}
        group={group}
        initialDraft={draft}
        defaultPayerId={getMyParticipantId(group, meQ.data)}
        submitLabel="Add expense"
        pendingLabel="Saving…"
        submitting={createExpense.isPending}
        serverError={
          createExpense.error
            ? "Failed to create expense. Please try again."
            : null
        }
        onCancel={() => navigate(groupPath)}
        onSubmit={async (values) => {
          await createExpense.mutateAsync({
            label: values.label,
            currency: values.currency,
            payments: values.payments,
            items: values.items.map((it) => ({
              label: it.label,
              cost: it.cost,
              split: it.split,
            })),
          });
          navigate(groupPath);
        }}
      />
    </div>
  );
}
